import {ReactComponent as IconChevron} from '#/components/icons/ico-chevron.svg';
import {ClassName, DefaultChildren} from '#/utils/utils';
import {ComponentProps, FunctionComponent, HTMLProps, ReactNode, forwardRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

type ContentTileProps = DefaultChildren & ClassName & ComponentProps<'div'>;
export const ContentTile = forwardRef<HTMLDivElement, ContentTileProps>((props, ref) => {
  const {children, className = '', ...rest} = props;
  return (
    <div ref={ref} className={`bg-graybluedark rounded-xl shadow-black p-4 ${className}`} {...rest}>
      {children}
    </div>
  );
});

type ContentTileWithTitleProps = {
  title: ReactNode;
  contentClassName?: String;
} & ClassName &
  DefaultChildren;

const contentTileStyle = {
  titleText: 'text-deepblue font-semibold text-sm uppercase',
};

export const ContentTileTitle: FunctionComponent<DefaultChildren> = ({children}) => (
  <span className={contentTileStyle.titleText}>{children}</span>
);

export const ContentTileWithTitle: FunctionComponent<ContentTileWithTitleProps> = ({
  children,
  title,
  className = '',
  contentClassName = '',
}) => {
  return (
    <ContentTile className={`space-y-2.5 ${className}`}>
      <ContentTileTitle>{title}</ContentTileTitle>

      <div className={`relative ${contentClassName}`}>{children}</div>
    </ContentTile>
  );
};

export const ContentTileWithTitleAndToggleSeparated: FunctionComponent<
  Omit<ContentTileWithTitleProps, 'children'> & {
    children: {
      opened: ReactNode;
      toOpen: ReactNode;
    };
    contentClassName?: string;
  }
> = ({children, title, className = '', contentClassName = ''}) => {
  const {t} = useTranslation('translation');
  const [isOpen, setIsOpen] = useState(false);
  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpen(!isOpen);
  };

  return (
    <ContentTile className={`relative pb-0 space-y-2.5 ${className}`}>
      <ContentTileWithTitleAndToggleButton title={title} isOpen={isOpen} onClick={onClick} />

      <div className={`overflow-hidden ${contentClassName}`}>
        <div className={`block ${contentClassName}`}>{children.opened}</div>
        <div className={`block pb-4 ${!isOpen ? 'h-10' : ''} ${contentClassName}`}>
          {!isOpen ? (
            <>
              {children.toOpen}
              <button
                className='absolute w-full flex justify-center items-center left-0 bottom-0 h-10 rounded-xl bg-gradient-to-t via-graybluedark/70 from-graybluedark text-xs text-deepblue group'
                onClick={onClick}
              >
                <span className='opacity-0 group-hover:opacity-100 transition duration-200'>
                  {t('actions.showMore')}
                </span>
              </button>
            </>
          ) : (
            children.toOpen
          )}
        </div>
      </div>
    </ContentTile>
  );
};

const ContentTileWithTitleAndToggleButton: FunctionComponent<
  {title: ReactNode; isOpen: boolean} & Omit<HTMLProps<HTMLButtonElement>, 'title'>
> = ({title, isOpen, ...buttonProps}) => (
  <button {...buttonProps} type='button' className={'w-full flex justify-between items-center hover:opacity-70'}>
    <ContentTileTitle>{title}</ContentTileTitle>
    <IconChevron className={`fill-deepblue stroke-deepblue ${isOpen ? '-scale-100' : ''}`} />
  </button>
);

export const ContentTileEmptyText: FunctionComponent<DefaultChildren> = ({children}) => (
  <div className='text-sm text-white/70 font-semibold text-center'>{children}</div>
);
